import type { BadgeTone } from '@/components/base/Badge';
import type { AuditCategory, AuditEvent, AuditImpact, AuditOutcome } from '@/types/portal';

const CATEGORY_TONES: Record<AuditCategory, BadgeTone> = {
  Money: 'accent',
  Access: 'primary',
  Campaign: 'secondary',
  Config: 'neutral',
  Security: 'outline',
};

export function auditCategoryTone(category: AuditCategory): BadgeTone {
  return CATEGORY_TONES[category] ?? 'neutral';
}

const IMPACT_TONES: Record<AuditImpact, BadgeTone> = {
  Standard: 'neutral',
  Sensitive: 'secondary',
  Critical: 'primary',
};

export function auditImpactTone(impact: AuditImpact): BadgeTone {
  return IMPACT_TONES[impact] ?? 'neutral';
}

const OUTCOME_TONES: Record<AuditOutcome, BadgeTone> = {
  Success: 'accent',
  Failed: 'primary',
  Denied: 'outline',
};

export function auditOutcomeTone(outcome: AuditOutcome): BadgeTone {
  return OUTCOME_TONES[outcome] ?? 'neutral';
}

export function clockOf(timestamp: string): string {
  return timestamp.slice(11, 16);
}

export function dayKeyOf(timestamp: string): string {
  return timestamp.slice(0, 10);
}

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** The date of the newest event in the set — treated as "today" for labels. */
export function latestDayKey(events: AuditEvent[]): string {
  return events.reduce(
    (max, event) => (dayKeyOf(event.timestamp) > max ? dayKeyOf(event.timestamp) : max),
    '',
  );
}

function shiftDay(key: string, delta: number): string {
  const [y, m, d] = key.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  date.setUTCDate(date.getUTCDate() + delta);
  const yy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

function dayLabel(key: string, todayKey: string): string {
  if (key === todayKey) return 'Today';
  if (key === shiftDay(todayKey, -1)) return 'Yesterday';
  const [y, m, d] = key.split('-').map(Number);
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

export interface AuditDayGroup {
  key: string;
  label: string;
  count: number;
  events: AuditEvent[];
}

/** Group events into day buckets, newest first, with friendly day labels. */
export function groupByDay(events: AuditEvent[], todayKey: string): AuditDayGroup[] {
  const map = new Map<string, AuditEvent[]>();
  events.forEach((event) => {
    const key = dayKeyOf(event.timestamp);
    const bucket = map.get(key);
    if (bucket) bucket.push(event);
    else map.set(key, [event]);
  });
  return Array.from(map.entries())
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .map(([key, bucket]) => ({
      key,
      label: dayLabel(key, todayKey),
      count: bucket.length,
      events: [...bucket].sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1)),
    }));
}

export interface AuditFilter {
  category: string;
  actorId: string;
  impact: string;
  query: string;
}

export function filterAudit(events: AuditEvent[], filter: AuditFilter): AuditEvent[] {
  const text = filter.query.trim().toLowerCase();
  return events.filter((event) => {
    if (filter.category !== 'All' && event.category !== filter.category) return false;
    if (filter.actorId !== 'All actors' && event.actorId !== filter.actorId) return false;
    if (filter.impact !== 'All impacts' && event.impact !== filter.impact) return false;
    if (text) {
      const haystack =
        `${event.actorName} ${event.actorRole} ${event.action} ${event.summary} ${event.resource} ${event.resourceId} ${event.reference} ${event.market}`.toLowerCase();
      if (!haystack.includes(text)) return false;
    }
    return true;
  });
}

export interface AuditTotals {
  total: number;
  today: number;
  highImpact: number;
  actors: number;
  attention: number;
}

export function auditTotals(events: AuditEvent[], todayKey: string): AuditTotals {
  const actorSet = new Set(events.map((event) => event.actorId));
  return {
    total: events.length,
    today: events.filter((event) => dayKeyOf(event.timestamp) === todayKey).length,
    highImpact: events.filter((event) => event.impact !== 'Standard').length,
    actors: actorSet.size,
    attention: events.filter((event) => event.outcome !== 'Success').length,
  };
}

export interface AuditCategoryRow {
  category: AuditCategory;
  count: number;
}

const CATEGORY_ORDER: AuditCategory[] = ['Money', 'Access', 'Campaign', 'Config', 'Security'];

export function auditCategoryBreakdown(events: AuditEvent[]): AuditCategoryRow[] {
  return CATEGORY_ORDER.map((category) => ({
    category,
    count: events.filter((event) => event.category === category).length,
  }));
}

export interface AuditActorRow {
  id: string;
  name: string;
  avatar: string;
  role: string;
  count: number;
}

export function auditTopActors(events: AuditEvent[], limit = 5): AuditActorRow[] {
  const map = new Map<string, AuditActorRow>();
  events.forEach((event) => {
    const existing = map.get(event.actorId);
    if (existing) existing.count += 1;
    else
      map.set(event.actorId, {
        id: event.actorId,
        name: event.actorName,
        avatar: event.actorAvatar,
        role: event.actorRole,
        count: 1,
      });
  });
  return Array.from(map.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
}

export interface AuditActorOption {
  id: string;
  name: string;
}

export function auditActorOptions(events: AuditEvent[]): AuditActorOption[] {
  const map = new Map<string, string>();
  events.forEach((event) => {
    if (!map.has(event.actorId)) map.set(event.actorId, event.actorName);
  });
  return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
}